import { getCompanyOptionWithPriceSelect } from '@/api/get/config/get-company-options-with-price-select';
import { getCompanyPriceListSelect } from '@/api/get/config/get-company-price-list-select';
import {
  PrimaryButton,
  PrimaryOutlineButton,
  TextButton,
} from '@/components/button/button-component';
import Card from '@/components/card/card-component';
import { Col, Row } from '@/components/grid/grid-component';
import Loader from '@/components/loader/loader-component';
import TemplateCategories from '@/components/product/product-categories/product-categories';
import TemplateDetailsForm from '@/components/product/product-details-form/product-details-form';
import LinkInput from '@/components/product/product-links/product-links';
import TemplateOptionsComponent from '@/components/product/product-option/product-options-component';
import FileUploader from '@/components/react-hook-form-inputs/file-dropzone/file-dropzone-component';
import ImageGallery from '@/components/react-hook-form-inputs/image-gallery/image-gallery-component';
import SelectSearch from '@/components/react-hook-form-inputs/select-search/select-search-component';
import ToggleSwitch from '@/components/react-hook-form-inputs/toggle-switch/toggle-switch-component';
import { Heading3Text } from '@/utils/global-fonts';
import { requiredValidationDropdownMessage } from '@/utils/validations';
import PropTypes from 'prop-types';
import { useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FiEye, FiX } from 'react-icons/fi';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import DocumentsUpload from '@/components/product/product-documents/product-document-upload';
import ConfirmationModal from '@/components/confirmation-modal/confirmation-modal';
import { BsCheck2 } from 'react-icons/bs';
import { FieldArrayRegistryProvider } from '@/components/product/product-option/context-provider';
import Styled from '../company-products/product-form-style';
import { TEMPLATE_CONTEXT } from './context-provider';
/**
 * @param {boolean} isLoading if the item is loading
 * @returns {JSX.Element}
 */
const TemplateForm = ({ isLoading, onApply }) => {
  const navigate = useNavigate();
  const { id, templateId } = useParams();
  const location = useLocation();
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const { setPrices, setOptions } = useContext(TEMPLATE_CONTEXT);
  const [loadingPrices, setLoadingPrices] = useState(true);
  const [loadingOptions, setLoadingOptions] = useState(true);

  useEffect(() => {
    getCompanyPriceListSelect(id).then((response) => {
      setPrices(response.data);
      setLoadingPrices(false);
    });
    getCompanyOptionWithPriceSelect(id).then((response) => {
      setOptions(response.data);
      setLoadingOptions(false);
    });
  }, [id, setOptions, setPrices]);

  const {
    watch,
    getValues,
    reset,
    formState: { errors, isLoading: formLoading, isDirty, dirtyFields },
  } = useFormContext();
  const optionStatus = watch('option.status');

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'invalid', label: 'Inactive' },
    { value: 'draft', label: 'Draft' },
  ];

  const clearProductData = () => {
    localStorage.setItem('templateFormValues', JSON.stringify({}));
  };

  const handleCancel = () => {
    if (templateId && isDirty && Object.keys(dirtyFields).length > 0) {
      ConfirmationModal({
        title: 'Confirm',
        text: `All unsaved changes will be lost. Are you sure you want to proceed?`,
        onConfirm: () => {
          clearProductData();
          navigate(-1);
        },
      });
    } else {
      clearProductData();
      navigate(-1);
    }
  };

  const refetchTemplate = (updatedTemplateData) => {
    reset(updatedTemplateData);
    setLoadingSubmit(false);
  };

  return isLoading || formLoading || loadingPrices || loadingOptions ? (
    <Styled.Wrapper>
      <Loader loading />
    </Styled.Wrapper>
  ) : (
    <Styled.Wrapper>
      <Col xs={12} md={8} lg={8}>
        <Row xs={12} md={12} lg={12}>
          <Card padding="8px 5px 5px 12px">
            <Col>
              <Row>
                <Heading3Text>Template Information</Heading3Text>
              </Row>
              <Row>
                <TemplateDetailsForm CONTEXT={TEMPLATE_CONTEXT} isTemplate />
              </Row>
            </Col>
          </Card>
          <Card
            padding="8px 5px 5px 12px"
            margin="16px 0 0 "
            $minHeight="600px"
          >
            <Col id="options-card">
              <Row>
                <Heading3Text>Template Options</Heading3Text>
              </Row>
              <Row>
                <FieldArrayRegistryProvider>
                  <TemplateOptionsComponent
                    name="templateOptions"
                    CONTEXT={TEMPLATE_CONTEXT}
                  />
                </FieldArrayRegistryProvider>
              </Row>
            </Col>
          </Card>
        </Row>
      </Col>
      <Styled.SideCol xs={12} md={4} lg={4}>
        <Card
          margin="0px 24px 16px 0px"
          padding={templateId ? '0px 0px' : '15px'}
        >
          <Styled.ActionsContainer noPadding={!templateId}>
            <Styled.Header>Actions</Styled.Header>

            <SelectSearch
              cy="selCompanyStatus"
              header="Status"
              name="template.status"
              label="Please select a status"
              options={statusOptions}
              icon={<Styled.StatusCircle status={optionStatus} />}
              required={{
                required: requiredValidationDropdownMessage,
              }}
            />
            <Styled.ActionWrapper>
              <Styled.LeftActionsWrapper>
                <PrimaryOutlineButton
                  buttonWidth="95px"
                  type="Button"
                  loading={isLoading}
                  title="Cancel"
                  onClick={handleCancel}
                  icon={<FiX />}
                  margin="0 0 0 0"
                />
              </Styled.LeftActionsWrapper>
              <Styled.RightActionsWrapper>
                {templateId && (
                  <PrimaryOutlineButton
                    buttonWidth="90px"
                    margin="0 8px 0 0"
                    type="Button"
                    loading={isLoading || loadingSubmit}
                    title="Apply"
                    onClick={() => {
                      if (!Object.keys(errors).length) {
                        const values = getValues();
                        onApply?.(values, (updatedTemplate) => {
                          setLoadingSubmit(true);
                          refetchTemplate(updatedTemplate);
                        });
                      }
                    }}
                    icon={<BsCheck2 />}
                  />
                )}
                <PrimaryButton
                  buttonWidth={templateId ? '116px' : '108px'}
                  type="submit"
                  loading={isLoading || loadingSubmit}
                  onClick={() => {
                    if (!errors) {
                      clearProductData();
                      setLoadingSubmit(!loadingSubmit);
                    }
                  }}
                  title={templateId ? 'Save & Close' : 'Save'}
                />
              </Styled.RightActionsWrapper>
            </Styled.ActionWrapper>
          </Styled.ActionsContainer>
          {templateId && (
            <Styled.ExtraActionsWrapper>
              <TextButton
                type="Button"
                loading={isLoading}
                title="Preview"
                onClick={() => {
                  const formValues = getValues();
                  localStorage.setItem(
                    'templateFormValues',
                    JSON.stringify(formValues),
                  );

                  window.open(`${location.pathname}/preview`, '_blank');
                }}
                icon={<FiEye />}
                margin="0 0 0 0"
              />
            </Styled.ExtraActionsWrapper>
          )}
        </Card>

        <Card padding="16px 18px 10px 18px" min-height="284px">
          <Styled.Header>Image Thumbnail</Styled.Header>
          <FileUploader
            headerText="Upload Thumbnail"
            name="template.thumbnail"
          />
        </Card>
        <Styled.CategoryWrapper>
          <Styled.CategoryContainer>
            <Styled.Header>Categories</Styled.Header>
            <TemplateCategories />
          </Styled.CategoryContainer>
        </Styled.CategoryWrapper>
        <Styled.GalleryWrapper>
          <Styled.UploadContainer $marginBottom="0px">
            <Card padding="8px 5px 5px 12px">
              <Styled.Header>Template Gallery</Styled.Header>
              <ImageGallery name="templateGallery" />
            </Card>
          </Styled.UploadContainer>
          <Styled.ActionArea>
            <ToggleSwitch
              label="Override Unreal Engine Images"
              name="template.override_unreal_images"
              registerOptions={{}}
            />
          </Styled.ActionArea>
        </Styled.GalleryWrapper>
        <Card>
          <Styled.DocumentUploadWrapper>
            <Styled.Header>Upload Documents</Styled.Header>
            <DocumentsUpload name="templateDocuments" />
          </Styled.DocumentUploadWrapper>
        </Card>
        <Card>
          <Styled.Header>Add Links</Styled.Header>
          <LinkInput name="templateLinks" />
        </Card>
      </Styled.SideCol>
    </Styled.Wrapper>
  );
};

TemplateForm.defaultProps = { isLoading: false, onApply: undefined };

TemplateForm.propTypes = {
  isLoading: PropTypes.bool,
  onApply: PropTypes.func,
};

export default TemplateForm;
